import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Link as GatsbyLink } from "gatsby";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import Container from "@components/Container";
import RichText from "@components/RichText";

import IconArrowRight from "@svgs/IconArrowRight";

import {
  ToolsSideBySideMain,
  ToolsSideBySideWrapper,
  ToolsSideBySideMainGrid,
  ToolsSideBySideLine,
  ToolsSideBySideItems,
  ToolsSideBySideItem,
  ToolsSideBySideImage,
  ToolsSideBySideContent,
  ToolsSideBySideLinks,
  ToolsSideBySideLink,
  ToolsSideBySideLinkImage,
  ToolsSideBySideLinkTitle,
  ToolsSideBySideLinkArrow,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const getAssetUrl = (asset) => {
  const url = asset?.file?.url;

  if (!url) return null;

  return url.startsWith("//") ? `https:${url}` : url;
};

const isExternalUrl = (url) =>
  /^(?:https?:)?\/\//i.test(url) ||
  url.startsWith("mailto:") ||
  url.startsWith("tel:");

const OurToolsSideBySide = ({ list, links, id }) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const linksListRef = useRef(null);
  const contentRefs = useRef([]);
  const linkRefs = useRef([]);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-tools-side-by-side";

  smoothScrollRef.current = store?.smoothScroll;

  const addContentRef = (element, index) => {
    contentRefs.current[index] = element;
  };

  const addLinkRef = (element, index) => {
    linkRefs.current[index] = element;
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const lineElement = lineRef.current;
    const linksList = linksListRef.current;
    const contentElements = contentRefs.current.filter(Boolean);
    const linkElements = linkRefs.current.filter(Boolean);

    if (!section) return undefined;

    const paragraphElements = contentElements.flatMap(
      (element) =>
        Array.from(
          element.querySelectorAll(
            "p, h1, h2, h3, h4, h5, h6",
          ),
        ),
    );

    let firstFrameId;
    let secondFrameId;

    const refresh = () => {
      if (
        smoothScrollRef.current &&
        typeof smoothScrollRef.current.refresh === "function"
      ) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const refreshAfterLayout = () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      firstFrameId = requestAnimationFrame(() => {
        secondFrameId = requestAnimationFrame(refresh);
      });
    };

    const resize = () => {
      gsap.matchMediaRefresh();
      refreshAfterLayout();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const paragraphTweens = [];

        if (lineElement) {
          gsap.set(lineElement, {
            scaleY: 0,
            transformOrigin: "top center",
            willChange: "transform",
          });
        }

        const lineTween = lineElement
          ? gsap.to(lineElement, {
              scaleY: 1,
              duration: 1.4,
              ease: "power2.inOut",
              clearProps:
                "transform,transformOrigin,willChange",
              scrollTrigger: {
                trigger: section,
                start: "top 75%",
                toggleActions: "play none none none",
                once: true,
                invalidateOnRefresh: true,
              },
            })
          : null;

        paragraphElements.forEach((paragraph) => {
          const paragraphTween = gsap.fromTo(
            paragraph,
            {
              autoAlpha: 0.15,
            },
            {
              autoAlpha: 1,
              ease: "none",
              scrollTrigger: {
                trigger: paragraph,
                start: "top 85%",
                end: "top 50%",
                scrub: true,
                invalidateOnRefresh: true,
              },
            },
          );

          paragraphTweens.push(paragraphTween);
        });

        let linksTween = null;

        if (linksList && linkElements.length > 0) {
          linksTween = gsap.fromTo(
            linkElements,
            {
              y: 60,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.8,
              stagger: 0.15,
              ease: "power3.out",
              clearProps:
                "transform,opacity,visibility,willChange",
              scrollTrigger: {
                trigger: linksList,
                start: "top 90%",
                toggleActions: "play none none none",
                once: true,
                invalidateOnRefresh: true,
              },
            },
          );
        }

        refreshAfterLayout();

        return () => {
          if (lineTween) {
            lineTween.scrollTrigger?.kill();
            lineTween.kill();
          }

          paragraphTweens.forEach((tween) => {
            tween.scrollTrigger?.kill();
            tween.kill();
          });

          if (linksTween) {
            linksTween.scrollTrigger?.kill();
            linksTween.kill();
          }
        };
      });

      mm.add("(max-width:1023px)", () => {
        const animatedElements = [
          lineElement,
          ...paragraphElements,
          ...linkElements,
        ].filter(Boolean);

        if (animatedElements.length > 0) {
          gsap.set(animatedElements, {
            clearProps: "all",
          });
        }
      });

      refreshAfterLayout();

      return () => {
        mm.revert();
      };
    }, section);

    window.addEventListener("load", refreshAfterLayout);
    window.addEventListener("resize", resize);

    return () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );
      window.removeEventListener("resize", resize);

      contentRefs.current = [];
      linkRefs.current = [];
    };
  }, [list, links, store?.smoothScroll]);

  return (
    <ToolsSideBySideMain
      id={sectionId}
      ref={sectionRef}
    >
      <Container>
        <ToolsSideBySideWrapper>
          <ToolsSideBySideMainGrid>
            <ToolsSideBySideLine
              ref={lineRef}
              aria-hidden="true"
            />

            <ToolsSideBySideItems>
              {list.map((item, index) => {
                const imageUrl = getAssetUrl(item?.image);
                const imageAlt =
                  item?.image?.description ||
                  item?.image?.title ||
                  "";

                return (
                  <ToolsSideBySideItem
                    key={
                      item?.id ||
                      item?.contentful_id ||
                      index
                    }
                  >
                    <ToolsSideBySideImage>
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={imageAlt}
                          loading="lazy"
                        />
                      )}
                    </ToolsSideBySideImage>

                    <ToolsSideBySideContent
                      ref={(element) =>
                        addContentRef(element, index)
                      }
                    >
                      {item?.text && (
                        <RichText content={item.text} />
                      )}
                    </ToolsSideBySideContent>
                  </ToolsSideBySideItem>
                );
              })}
            </ToolsSideBySideItems>
          </ToolsSideBySideMainGrid>

          <ToolsSideBySideLinks ref={linksListRef}>
            {links.map((link, index) => {
              const linkUrl = link?.url || "#";
              const imageUrl = getAssetUrl(link?.image);
              const imageAlt =
                link?.image?.description ||
                link?.image?.title ||
                "";
              const external = isExternalUrl(linkUrl);

              const linkProps = external
                ? {
                    as: "a",
                    href: linkUrl,
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }
                : {
                    as: GatsbyLink,
                    to: linkUrl,
                  };

              return (
                <ToolsSideBySideLink
                  key={
                    link?.id ||
                    link?.contentful_id ||
                    index
                  }
                  ref={(element) =>
                    addLinkRef(element, index)
                  }
                  {...linkProps}
                >
                  <span className="linkCont">
                    {imageUrl && (
                      <ToolsSideBySideLinkImage>
                        <img
                          src={imageUrl}
                          alt={imageAlt}
                          loading="lazy"
                        />
                      </ToolsSideBySideLinkImage>
                    )}

                    <ToolsSideBySideLinkArrow
                      aria-hidden="true"
                    >
                      <IconArrowRight />
                    </ToolsSideBySideLinkArrow>
                  </span>

                  <ToolsSideBySideLinkTitle>
                    {link?.textOld?.textOld}
                  </ToolsSideBySideLinkTitle>
                </ToolsSideBySideLink>
              );
            })}
          </ToolsSideBySideLinks>
        </ToolsSideBySideWrapper>
      </Container>
    </ToolsSideBySideMain>
  );
};

OurToolsSideBySide.propTypes = {
  id: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      text: PropTypes.object,
      image: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
          contentType: PropTypes.string,
        }),
      }),
    }),
  ),
  links: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      url: PropTypes.string,
      image: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
          contentType: PropTypes.string,
        }),
      }),
      textOld: PropTypes.shape({
        textOld: PropTypes.string,
      }),
    }),
  ),
};

OurToolsSideBySide.defaultProps = {
  id: "our-tools-side-by-side",
  list: [],
  links: [],
};

export default OurToolsSideBySide;